import { useToast } from "@/hooks/use-toast";
import { useAuthStore } from "@/stores/authStore";
import { useLoginWithGoogle } from "@/tanstack-hooks";
import { TRANSLATIONS } from "@/translations";
import { TokenResponse, useGoogleLogin } from "@react-oauth/google";
import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Capacitor } from "@capacitor/core";
import { SocialLogin } from "@capgo/capacitor-social-login";
import googleIcon from "../../assets/google.svg";
import { Button } from "../ui/button";
import SpinnerSmall from "../SpinnerSmall";
import { isIOS, isYandex } from "react-device-detect";
import { InfoIcon } from "lucide-react";
import { useLocaleTranslation } from "@/hooks/useLocaleTranslation";
import { SUCCESSFUL_LOGIN_TYPE, SuccessfulLoginType } from "./SignInFlowTypes";
import { clearStoredUtms, getStoredUtms } from "@/lib/utms";

type SubmitToken = (accessToken: string) => void;

function useSubmitGoogleToken(fingerprint: string) {
  const { toast } = useToast();
  const { setToken } = useAuthStore();
  const navigate = useNavigate();
  const loginWithGoogleMutation = useLoginWithGoogle();

  const submit: SubmitToken = (accessToken) => {
    loginWithGoogleMutation.mutate(
      { id_token: accessToken, fingerprint, ...getStoredUtms() },
      {
        onSuccess: (data) => {
          setToken(data.access_token);
          localStorage.setItem(
            SUCCESSFUL_LOGIN_TYPE,
            SuccessfulLoginType.GOOGLE,
          );
          clearStoredUtms();
          navigate("/");
        },
        onError: () => {
          toast({
            variant: "destructive",
            title: "Error logging in with google",
            description: TRANSLATIONS.ENGLISH.SHARED.ERRORS.PERSISTENT,
            className: "dark",
          });
        },
      },
    );
  };

  return { submit, isPending: loginWithGoogleMutation.isPending };
}

function GoogleButton({
  onClick,
  isPending,
}: {
  onClick: () => void;
  isPending: boolean;
}) {
  return (
    <Button onClick={onClick} disabled={isPending}>
      {isPending ? (
        <SpinnerSmall black={true} />
      ) : (
        <img src={googleIcon} alt="Google Logo" className="w-8" />
      )}
    </Button>
  );
}

function LoginWithGoogleWeb({ fingerprint }: { fingerprint: string }) {
  const { toast } = useToast();
  const t = useLocaleTranslation();
  const { submit, isPending } = useSubmitGoogleToken(fingerprint);

  function handleSuccess(
    tokenResponse: Omit<
      TokenResponse,
      "error" | "error_description" | "error_uri"
    >,
  ) {
    const accessToken = tokenResponse.access_token;
    if (!accessToken) {
      toast({
        variant: "destructive",
        title: "Error logging in with google",
        description: TRANSLATIONS.ENGLISH.SHARED.ERRORS.PERSISTENT,
        className: "dark",
      });
      return;
    }
    submit(accessToken);
  }

  const login = useGoogleLogin({
    onSuccess: handleSuccess,
    onError: () => {
      toast({
        variant: "destructive",
        title: "Error logging in with Google",
        description: TRANSLATIONS.ENGLISH.SHARED.ERRORS.PERSISTENT,
        className: "dark",
      });
    },
    flow: "implicit",
    scope: "email",
  });

  function loginOrToast() {
    if (isIOS && isYandex) {
      toast({
        variant: "default",
        // @ts-expect-error Type 'Element' is not assignable to type
        title: (
          <div className="flex items-center gap-2">
            <InfoIcon className="h-4 w-4" />
            <span>{t.LOGIN_EMAIL_CARD.ERRORS.GOOGLE_NOT_SUPPORTED_TITLE}</span>
          </div>
        ),
        description: t.LOGIN_EMAIL_CARD.ERRORS.GOOGLE_NOT_SUPPORTED_DESCRIPTION,
        className: "dark rounded-2xl",
        duration: 9000,
      });
      return;
    }
    login();
  }

  return <GoogleButton onClick={loginOrToast} isPending={isPending} />;
}

function LoginWithGoogleNative({ fingerprint }: { fingerprint: string }) {
  const { toast } = useToast();
  const { submit, isPending } = useSubmitGoogleToken(fingerprint);

  useEffect(() => {
    void SocialLogin.initialize({
      google: {
        webClientId: import.meta.env.VITE_GOOGLE_CLIENT_ID,
        iOSClientId: import.meta.env.VITE_GOOGLE_IOS_CLIENT_ID,
      },
    });
  }, []);

  async function handleLogin() {
    try {
      const res = await SocialLogin.login({
        provider: "google",
        options: { scopes: ["email"], forcePrompt: true },
      });
      const result = res.result as {
        accessToken?: { token?: string } | string | null;
      };
      const raw = result.accessToken;
      const accessToken =
        typeof raw === "string" ? raw : (raw?.token ?? null);

      if (!accessToken) {
        toast({
          variant: "destructive",
          title: "Error logging in with google",
          description: TRANSLATIONS.ENGLISH.SHARED.ERRORS.PERSISTENT,
          className: "dark",
        });
        return;
      }
      submit(accessToken);
    } catch (error) {
      if (
        error &&
        typeof error === "object" &&
        "message" in error &&
        typeof error.message === "string" &&
        (error.message.includes("canceled") ||
          error.message.includes("cancelled") ||
          error.message.includes("user_cancelled"))
      ) {
        console.log("User dismissed Google Sign-In");
        return;
      }
      console.error("Google Sign-In error:", error);
      toast({
        variant: "destructive",
        title: "Error logging in with Google",
        description: TRANSLATIONS.ENGLISH.SHARED.ERRORS.PERSISTENT,
        className: "dark",
      });
    }
  }

  return (
    <GoogleButton onClick={() => void handleLogin()} isPending={isPending} />
  );
}

export default function LoginWithGoogle({
  fingerprint,
}: {
  fingerprint: string;
}) {
  return Capacitor.isNativePlatform() ? (
    <LoginWithGoogleNative fingerprint={fingerprint} />
  ) : (
    <LoginWithGoogleWeb fingerprint={fingerprint} />
  );
}
